import React from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { useMutation, useQueryClient } from 'react-query';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

import * as Api from '../api';
import * as Types from '../types';

interface IFormValues extends Types.IForm.SendReport {}

interface IChildren extends FormikProps<IFormValues> {}

interface IProps {
  registerId: number;
  className?: string;
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
  onSettled?: () => void;
  children(props: IChildren): JSX.Element;
}

const Create: React.FC<IProps> = ({ registerId, onSuccess, onError, onSettled, children }) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const mutation = useMutation<any, string, IFormValues, any>(
    async values => {
      const { data } = await Api.SendReport({ values });

      return data && data.data;
    },
    {
      onSuccess: data => {
        setTimeout(() => {
          queryClient.invalidateQueries(['report-history', 'list', registerId]);
        }, 1000);

        onSuccess && onSuccess(data);
      },
      onError,
      onSettled
    }
  );

  const validationSchema = yup.object().shape<IFormValues>({
    registerId: yup.number().required(),
    text: yup.string().max(4000, t('text must be less than 4000 characters')).required(t('text is a required field')),
    reportFileId: yup.number().nullable().required(t('reportFileId is a required field'))
  });

  const handleSubmit = (values: IFormValues, { setSubmitting }: FormikHelpers<IFormValues>) => {
    setSubmitting(true);
    mutation.mutate(values, {
      onSettled: () => setSubmitting(false)
    });
  };

  return (
    <Formik<IFormValues>
      onSubmit={handleSubmit}
      initialValues={{
        registerId,
        text: '',
        reportFileId: null
      }}
      {...{ validationSchema }}
    >
      {(props: FormikProps<IFormValues>) => <Form>{children(props)}</Form>}
    </Formik>
  );
};

export default Create;
